import React from "react";
import { generateUuid7 } from "@/utils/uuid7";
import { captureError } from "./capture";

export type ErrorBoundaryScope = "root" | "route" | "panel";

export type ErrorBoundaryFallbackProps = {
  error: Error;
  componentStack: string | null;
  eventId: string;
  scope: ErrorBoundaryScope;
  reset: () => void;
};

type Props = {
  children: React.ReactNode;
  scope?: ErrorBoundaryScope;
  fallback:
    | React.ReactNode
    | ((props: ErrorBoundaryFallbackProps) => React.ReactNode);
  onReset?: () => void;
  resetKeys?: ReadonlyArray<unknown>;
};

type State = {
  error: Error | null;
  componentStack: string | null;
  eventId: string;
};

const initialState: State = {
  error: null,
  componentStack: null,
  eventId: "",
};

function arraysShallowEqual(
  a: ReadonlyArray<unknown> | undefined,
  b: ReadonlyArray<unknown> | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) {
      return false;
    }
  }
  return true;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = initialState;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, componentStack: null, eventId: generateUuid7() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const componentStack = info.componentStack ?? null;
    this.setState({ componentStack });
    // Keep existing console-based diagnostics so devs see crashes in the console.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scope ?? "panel", error, info);
    // Auto-report every crash (fire-and-forget) so we get data without relying
    // on the user clicking "Report". Reuses the eventId shown in the fallback
    // so the on-screen Reference ID maps to a real, queryable report.
    captureError({
      error,
      componentStack,
      eventId: this.state.eventId,
      scope: this.props.scope ?? "panel",
      source: "boundary",
    });
  }

  componentDidUpdate(prevProps: Props): void {
    if (
      this.state.error &&
      !arraysShallowEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState(initialState);
    this.props.onReset?.();
  };

  render(): React.ReactNode {
    const { error, componentStack, eventId } = this.state;
    const { children, fallback, scope = "panel" } = this.props;

    if (!error) {
      return children;
    }

    if (typeof fallback === "function") {
      return fallback({
        error,
        componentStack,
        eventId,
        scope,
        reset: this.reset,
      });
    }

    return fallback;
  }
}
